import type { CutOffDate, ScheduleResponse } from "@/types/api";

export type ScheduleCutoffSource = Pick<
  ScheduleResponse,
  "matched" | "cut_off_dates" | "cargo_cutoff"
> & {
  legs?: Array<{ cut_off_dates?: unknown }>;
};

/** Operator-friendly label for a DCSA `cutOffDateTimeCode`. */
export function dcsaCutoffLabel(code: string): string {
  switch (code.trim().toUpperCase()) {
    case "FCO":
      return "Full Container Cut-off";
    case "DCO":
      return "Documentation Cut-off";
    case "VCO":
      return "VGM Cut-off";
    case "ECP":
      return "Empty Container Pickup";
    case "LCO":
      return "Late Gate-in Cut-off";
    case "ECO":
      return "Empty Container Cut-off";
    case "RCO":
      return "Reefer Cut-off";
    case "AFD":
      return "Advance Filing Cut-off";
    case "PCO":
      return "Port Cut-off";
    default:
      return code.trim() || "Cut-off";
  }
}

/**
 * Parse a schedule `cut_off_dates` payload into `{ name, date_time, code }`
 * rows. Accepts both the canonical shape (`name` / `date_time`, with `date` as
 * an alias) and the raw DCSA shape (`cutOffDateTimeCode` / `cutOffDateTime`) so
 * Maersk rows ingested before normalisation still render.
 */
export function parseCutOffDates(raw: unknown): CutOffDate[] {
  if (!Array.isArray(raw)) return [];
  const out: CutOffDate[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== "object") continue;
    const row = item as {
      name?: unknown;
      date_time?: unknown;
      date?: unknown;
      code?: unknown;
      cutOffDateTime?: unknown;
      cutOffDateTimeCode?: unknown;
    };
    const date_time =
      typeof row.date_time === "string"
        ? row.date_time
        : typeof row.date === "string"
          ? row.date
          : typeof row.cutOffDateTime === "string"
            ? row.cutOffDateTime
            : undefined;
    const code =
      typeof row.code === "string"
        ? row.code
        : typeof row.cutOffDateTimeCode === "string"
          ? row.cutOffDateTimeCode
          : undefined;
    const name =
      typeof row.name === "string"
        ? row.name
        : code
          ? dcsaCutoffLabel(code)
          : undefined;
    if (!date_time && !name) continue;
    out.push({ name, date_time, ...(code ? { code } : {}) });
  }
  return out;
}

/** Cutoffs on matched sailings (schedule-level + every leg). */
export function flattenMatchedCutoffs(
  schedules: ScheduleCutoffSource[] | undefined,
): CutOffDate[] {
  if (!schedules?.length) return [];
  const matched = schedules.filter((s) => s.matched);
  const out: CutOffDate[] = [];
  for (const schedule of matched) {
    out.push(...parseCutOffDates(schedule.cut_off_dates));
    for (const leg of schedule.legs ?? []) {
      out.push(...parseCutOffDates(leg.cut_off_dates));
    }
  }
  return out.filter((c) => Boolean(c.date_time));
}

function cutoffTime(entry: CutOffDate): number | null {
  if (!entry.date_time) return null;
  const time = new Date(entry.date_time).getTime();
  return Number.isNaN(time) ? null : time;
}

export function earliestCutoff(cutoffs: CutOffDate[]): CutOffDate | undefined {
  let best: CutOffDate | undefined;
  let bestTime = Number.POSITIVE_INFINITY;
  for (const entry of cutoffs) {
    const time = cutoffTime(entry);
    if (time == null || time >= bestTime) continue;
    best = entry;
    bestTime = time;
  }
  return best;
}

/** SI / CY / doc cutoff — the same fallback v1 uses on the booking overview. */
export function earliestSiCyCutoff(
  cutoffs: CutOffDate[],
): CutOffDate | undefined {
  return earliestCutoff(
    cutoffs.filter((c) => {
      const name = (c.name ?? "").toLowerCase();
      return name.includes("si") || name.includes("cy") || name.includes("doc");
    }),
  );
}

export function normalizeCutoffName(name?: string): string {
  return (name ?? "")
    .replace(/Cut-?off/gi, "Cutoff")
    .replace(/cut_off/gi, "Cutoff")
    .trim();
}

/** Native caption, e.g. "Schedule · SI Cutoff". */
export function scheduleCutoffCaption(name?: string): string {
  const cleaned = normalizeCutoffName(name);
  return cleaned ? `Schedule · ${cleaned}` : "Schedule match";
}
